/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UtilsService } from '../../services';

@Component({
    selector: 'app-search-rows',
    styleUrls: ['./search-rows.component.scss'],
    templateUrl: './search-rows.component.html'
})

export class SearchRowsComponent {
    /**
     * Set the predefined search term.
     * @param value
     */
    @Input() set predefinedSearchTerm(value: (string | any[])) {
        this.searchTerm = value;
        const searchEvent = { target: { value } };
        if (typeof value === 'string' && value.includes(',')) {
            searchEvent.target.value = value.split(',');
        }
        this.setUpSearch(searchEvent);
    }

    /**
     * Hide search term for predefined search term.
     */
    @Input() public hideSearchTerm: boolean = false;

    /**
     * An array of rows to search.
     * @param rows
     */
    @Input() set rows(rows: any[]) {
        // Only set if the local variable is empty.
        // 20/02/23 Length compare added, so you can update the rows if the content is the same but something like a status has changed.
        if ((this._rows && !this._rows.length) || (this._rows.length === rows.length)) {
            this._rows = rows;
            this.clonedRows = [...this._rows];
        }
    }

    /**
     * The object keys to search on.
     */
    @Input() public searchKeys: any[] = [];

    /**
     * The search input placeholder text.
     */
    @Input() public placeholder: string = 'search_placeholder';

    /**
     * Edge case usage to embed the search with other form elements.
     */
    @Input() public removeMargin: boolean = false;

    /**
     * Output of searched rows.
     */
    @Output() public searchResults: EventEmitter<any[]> = new EventEmitter();

    /**
     * Output of matched keys.
     */
    @Output() public matchedKeys: EventEmitter<any[]> = new EventEmitter();

    /**
     * Output of search values from the internal search field.
     */
    @Output() public searchValues: EventEmitter<any[]> = new EventEmitter();

    /**
     * Store the search term.
     */
    public searchTerm: (string | number | boolean | any[]);

    /**
     * Return first result only.
     */
    public returnSingleton = false;

    /**
     * To store the cloned rows.
     * @private
     */
    private clonedRows: any[] = [];

    /**
     * Store rows to search
     * @private
     */
    private _rows: any[] = [];

    /**
     * Store the matched keys ready for output to parent.
     * @private
     */
    private keyMatches = [];

    constructor(
        private utilsService: UtilsService
    ) {
    }

    /**
     * Enable parent components to clear the rows variable.
     */
    public clearRows() {
        this._rows = [];
    }

    /**
     * When search is cleared, reset the relevant search.
     */
    public clearSearch(): void {
        this.searchTerm = null;
        this.keyMatches = [];
        this.searchResults.emit(this.clonedRows);
        this.matchedKeys.emit(this.keyMatches);
        this.searchValues.emit([]);
    }

    /**
     * Set up the search.
     * @param event
     */
    public setUpSearch(event: any): void {
        if (!event) {
            this.searchResults.emit(this.clonedRows);
        } else {
            const searchTerms = this.cleanArray(this.convertToArray(event.target.value));
            this.searchValues.emit(searchTerms);
            this.keyMatches = [];
            let results: any[] = [];

            if (searchTerms?.length) {
                for (const searchTerm of searchTerms) {
                    results = results.concat(this.searchRows(searchTerm));
                }
            } else {
                results = this.clonedRows;
            }

            this.searchResults.emit(this.utilsService.dedupe(results));
            this.matchedKeys.emit(this.keyMatches);
        }
    }

    /**
     * Check if search value contains comma.
     * @param value
     * @private
     */
    private convertToArray(value: any): string[] {
        if (Array.isArray(value)) {
            return value;
        } else if (typeof value === 'string' && value.includes(',')) {
            return value.split(',');
        } else {
            return [value];
        }
    }

    /**
     * Remove empty array items and trim white space off others.
     * @param valuesArray
     * @private
     */
    private cleanArray(valuesArray: any[]): string[] {
        const cleanArray = [];
        for (const value of valuesArray) {
            if (typeof value === 'string') {
                if (value && value.length > 0) {
                    const removedSpecialCharacters = value.replace(/[^a-zA-Z':._/\- \d]/g, '');
                    cleanArray.push(removedSpecialCharacters.trim());
                }
            } else {
                cleanArray.push(value);
            }
        }

        return cleanArray;
    }

    /**
     * Search rows.
     * @param searchTerm
     * @private
     */
    private searchRows(searchTerm: string): any {
        const results = [];

        for (const row of this.clonedRows) {
            if (this.searchKeysArray(row, this.formatSearchTerm(searchTerm))) {
                results.push(row);
                if (this.returnSingleton) {
                    break;
                }
            }
        }

        // We should reset the return singleton every time as it is not the default behaviour.
        this.returnSingleton = false;

        return results;
    }

    /**
     * Format the search term.
     * @param searchTerm
     * @private
     */
    private formatSearchTerm(searchTerm: string | RegExp): any {
        if (searchTerm === null || searchTerm === undefined) {
            return searchTerm;
        } else {
            return new RegExp(searchTerm, 'gi');
        }
    }

    /**
     * Search object keys.
     * @param row
     * @param searchText
     * @private
     */
    private searchKeysArray(row: any, searchText: any): boolean {
        for (const key of this.searchKeys) {
            if (row.hasOwnProperty(key)) {
                if (row[key] === null || row[key] === undefined || searchText === null || searchText === undefined) {
                    if (row[key] === searchText) {
                        this.addMatchedKeys(key);

                        return true;
                    }
                } else {
                    if (searchText.test(row[key].toString())) {
                        this.addMatchedKeys(key);

                        return true;
                    }
                }
            }
        }
    }

    /**
     * Build array of matched keys.
     * @param key
     * @private
     */
    private addMatchedKeys(key: any): void {
        if (!this.keyMatches.includes(key)) {
            this.keyMatches.push(key);
        }
    }
}
